/**
 * La memoria NEGATIVA delle resource: «questa resource non me la danno, smetti di chiederla
 * per questa sessione».
 *
 * 🔴 IL FATTO [M or45, 2026-09-06 su `stg-tma`, traffico spontaneo della app]. In un giro di
 * cinque aperture di pannelli della Hub partono **sette** `POST /oidc/token`: una a `200`
 * (il refresh dell'identita', resource `api.tournaments`) e **sei a `400`** con
 * `{"code":"oidc.invalid_target","error_description":"resource indicator is missing, or
 * unknown"}` per `resource=https://qduels.qlash.gg/api` — una resource che sul tenant di
 * staging **non esiste**, il che e' atteso.
 * ⚠️ Quest'ultima frase e' la misura di or45 come l'aveva scritta, e la lascio sua. Ma alla
 * luce delle DUE cause qui sotto **non e' piu' decidibile da questo 400 soltanto**: lo stesso
 * errore esce anche per una resource che sul tenant c'e' e che manca alla lista di quel
 * grant. Non la riscrivo perche' non l'ho rimisurata io — la marco perche' nessuno la citi
 * come prova che qduels sia assente dal tenant.
 *
 * Il difetto non e' il 400: e' la **ripetizione**. La app rifa' a ogni montaggio una
 * richiesta che ha gia' fallito per un motivo che non cambia da solo, e non se ne ricorda.
 *
 * ⚠️ **E `invalid_target` NON e' `invalid_grant`.** Sono due 400 che si assomigliano e non
 * hanno niente in comune:
 *  · `invalid_grant` dice che il **grant condiviso** e' morto (o che due redenzioni si sono
 *    incrociate): riguarda la sessione INTERA, si cura da solo in un caso e nell'altro
 *    porta al ramo terminale. Memorizzarlo qui congelerebbe una sessione viva.
 *  · `invalid_target` parla della **resource**, non della sessione: la sessione sta
 *    benissimo, ed e' l'unico dei due che non puo' cambiare esito ritentando SUBITO.
 * ⇒ La lista dei codici memorizzabili sta QUI dentro e non nel chiamante, cosi' il divieto
 * e' verificabile: aggiungerci `invalid_grant` deve far arrossare il gate, non passare come
 * una riga di configurazione.
 *
 * ═══ 🔴 `invalid_target` HA DUE CAUSE, E I DUE RIMEDI SONO OPPOSTI ═══
 * ⚠️ Questa riga diceva una cosa sola — «quella resource non esiste sul tenant» — ed e'
 * INCOMPLETA nel verso che costa: manda chi legge a chiedere all'amministratore di creare
 * una resource **che c'e' gia'**.
 *
 *  1. **NON REGISTRATA SUL TENANT.** Rimedio: la crea l'amministratore del tenant. Finche'
 *     non lo fa, nessun login la ottiene.
 *  2. **REGISTRATA, MA NON NELLA LISTA DELL'`authorize` DI QUEL GRANT.** Rimedio:
 *     **RIFARE IL LOGIN.** Nessun amministratore deve toccare niente.
 *     [M or49, 06/09 23:19-23:25Z — trasportato da or35, NON verificato da me]: nel fork
 *     oidc-provider di Logto (`resolve_resource.js:28`) il controllo e'
 *     `if (resource && !model.resourceIndicators.has(resource)) throw InvalidTarget`, e
 *     `model` **e' il refresh token**. ⇒ al refresh si guarda la lista di resource
 *     dell'**authorize originale**, NON il registro del tenant.
 *     🔑 Quindi «resource indicator is missing, or unknown» al REFRESH significa **«non nel
 *     TOKEN»**, non «non nel TENANT». Una resource aggiunta al tenant oggi non entra nelle
 *     sessioni nate ieri: quelle continuano a ricevere 400 finche' non rifanno il login.
 *
 * ═══ COME SI DISTINGUONO, e cosa NON si puo' fare ═══
 * ⛔ **Dai log di Logto non si distinguono**: `/api/logs` non ha audit di create / update /
 * delete delle resource [M or49, 06/09, trasportato]. Cercare li' e' mandare qualcuno dove
 * non c'e' niente — ed e' il motivo per cui questa riga lo dice invece di tacere.
 * ✅ **Il discriminante pratico, che si legge dal client** [M tma5, 06/09 23:1xZ su
 * `stg-tma`]: guarda se un ALTRO token per-servizio e' presente nello stesso storage. Nella
 * sonda che ha aperto questo caso `qlash.qbot_access_token` c'era e quello qgammon mancava.
 * Se la causa fosse (1), l'esito di qbot non c'entrerebbe nulla; con (2) qbot c'e' perche'
 * la SUA resource era gia' nella lista quando quel grant e' nato. ⇒ due token per-servizio
 * con esiti diversi sullo stesso storage indicano la lista del GRANT, non il tenant.
 * ⚠️ E' un indizio forte, non una prova: la prova sta nel tenant, e il tenant non lo vediamo
 * da qui.
 *
 * ⇒ **Il comportamento di questo modulo non cambia**, ed e' giusto cosi' in entrambe le
 * cause: con (1) la resource non arrivera' comunque, con (2) non arrivera' **a questo
 * grant** — e un login nuovo e' una sessione nuova, quindi una memoria nuova.
 *
 * ⚠️ **Con una scadenza, e non e' pignoleria.** Una resource assente oggi puo' essere creata
 * domani dall'amministratore del tenant, e una memoria eterna la terrebbe spenta per tutta
 * la sessione senza che nessuno capisca perche'. Trenta minuti: abbastanza da togliere la
 * ripetizione, poco da non nascondere una configurazione cambiata.
 * ⚠️ Nota che la scadenza aiuta la causa (1) e NON la (2): una resource creata mentre la app
 * e' aperta viene ritentata dopo mezz'ora e stavolta arriva; una che manca alla lista di
 * QUESTO grant continuera' a mancare a ogni ritentativo, perche' il grant non cambia. Li'
 * l'unica uscita e' un login nuovo.
 *
 * In memoria e per sessione: niente `localStorage`. Una resource creata mentre l'utente ha
 * la app aperta si recupera al reload, e uno stato negativo che sopravvive alla chiusura
 * della app e' esattamente il genere di cosa che poi nessuno riesce a spiegare.
 */

/** Mezz'ora. Vedi il cappello: e' un compromesso, non una costante fisica. */
export const RESOURCE_MEMO_TTL_MS = 30 * 60 * 1000;

/**
 * I codici d'errore che descrivono la RESOURCE e non la sessione. Uno solo, oggi, ed e'
 * volutamente un elenco chiuso: un codice sconosciuto non si memorizza.
 */
const CODICI_DI_RESOURCE = new Set(['invalid_target']);

export function isMemorableResourceCode(code: string): boolean {
  return CODICI_DI_RESOURCE.has(code);
}

export interface ResourceMemo {
  /**
   * Registra un fallimento. Ritorna `true` solo se e' stato memorizzato — cosi' il
   * chiamante puo' distinguere «me ne ricordero'» da «questo non e' affar mio».
   */
  markUnavailable(resource: string, code: string, now: number): boolean;
  /** `true` se la resource e' stata dichiarata assente e la memoria non e' ancora scaduta. */
  isUnavailable(resource: string, now: number): boolean;
  /** Dimentica tutto: login, logout, cambio account. */
  clear(): void;
  /** Quante resource sono ricordate come assenti (dopo la potatura degli scaduti). */
  size(now: number): number;
}

export function createResourceMemo(ttlMs: number = RESOURCE_MEMO_TTL_MS): ResourceMemo {
  const assenti = new Map<string, { code: string; at: number }>();

  const scaduta = (voce: { at: number }, now: number): boolean => now - voce.at >= ttlMs;

  return {
    markUnavailable(resource, code, now) {
      if (!isMemorableResourceCode(code)) return false;
      // ⚠️ Si sovrascrive `at` anche se la voce c'e' gia': la scadenza vale dall'ULTIMO
      // rifiuto. Tenere il primo farebbe scadere la memoria mentre il server continua a
      // dire di no, e la ripetizione tornerebbe a giri di trenta minuti.
      assenti.set(resource, { code, at: now });
      return true;
    },
    isUnavailable(resource, now) {
      const voce = assenti.get(resource);
      if (voce === undefined) return false;
      if (scaduta(voce, now)) {
        // Si pota qui: una memoria scaduta che resta nella mappa e' un `size()` che mente.
        assenti.delete(resource);
        return false;
      }
      return true;
    },
    clear() {
      assenti.clear();
    },
    size(now) {
      for (const [resource, voce] of [...assenti]) {
        if (scaduta(voce, now)) assenti.delete(resource);
      }
      return assenti.size;
    },
  };
}

/** L'istanza dell'applicazione: una sola, perche' il tenant e' uno solo. */
export const resourceMemo = createResourceMemo();
